import { ZapIcon } from '@vizit/icons';
import { colors, cva, cx } from '@vizit/theme';
import * as React from 'react';
import { Button } from 'react-aria-components';
import { Tooltip, TooltipTrigger } from '../tooltip';

interface ImpactBadgeProps {
  /**
   * Number of filled zaps (0-4)
   * 0 = no impact, 1 = low impact, 2 = moderate impact, 3 = high impact, 4 = very high impact
   */
  filledCount: number;
  /**
   * Additional class name for styling
   */
  className?: string;
}

const classes = cva({
  base: 'inline-flex items-center gap-1 cursor-default',
});

const getImpactLabel = (count: number): string => {
  switch (count) {
    case 0:
      return 'No impact';
    case 1:
      return 'Low impact';
    case 2:
      return 'Moderate impact';
    case 3:
      return 'High impact';
    case 4:
      return 'Very high impact';
    default:
      return 'No impact';
  }
};

/**
 * A badge component that displays impact level using 4 zap icons
 * Filled zaps indicate higher impact
 * Displays a tooltip on hover showing the impact level
 *
 * @example
 * <ImpactBadge filledCount={2} />
 */
const ImpactBadge = React.forwardRef<HTMLButtonElement, ImpactBadgeProps>((props, forwardedRef) => {
  const { filledCount, className } = props;

  const clampedCount = Math.max(0, Math.min(4, filledCount));
  const impactLabel = getImpactLabel(clampedCount);

  return (
    <TooltipTrigger delay={200}>
      <Button ref={forwardedRef} className={cx(classes(), className)} data-testid="impact-badge">
        {[0, 1, 2, 3].map((index) => (
          <ZapIcon
            key={index}
            size={14}
            color={index < clampedCount ? colors['gray-utility']['500'] : colors['gray-utility']['200']}
          />
        ))}
      </Button>
      <Tooltip>{impactLabel}</Tooltip>
    </TooltipTrigger>
  );
});

export type { ImpactBadgeProps };
export { ImpactBadge };
